<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import type { OnboardingAuditStatus, RecaptureOnboardingAuditResponse } from '@/services/api/onboarding-audit.api'

import cartseeLogo from '@/assets/icons/integrations/cartsee.png'
import manbaLogo from '@/assets/icons/integrations/manba.png'
import klaviyoLogo from '@/assets/klaviyo.png'
import felixPortrait from '@/assets/pics/felix.png'
import narwalLogo from '@/assets/pics/narwal-logo.png'
import rebeccaPortrait from '@/assets/pics/rebecca.png'
import sylvoxLogo from '@/assets/pics/sylvox-1.png'
import { Icon } from '@/components/icon'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { useAmplitude } from '@/composables/use-amplitude'
import OnboardingRetentionLayout from '@/pages/onboarding-retention/components/OnboardingRetentionLayout.vue'
import { useAdsAccountList } from '@/services/api/ads.api'
import { recaptureOnboardingAuditApi } from '@/services/api/onboarding-audit.api'
import {
  RETENTION_ESP_META,
  useOnboardingRetentionProgressStore,
} from '@/stores/onboarding-retention-progress'

definePage({
  path: '/onboarding-retention/step1',
  meta: { layout: false },
})

const router = useRouter()
const route = useRoute()
const { t, locale } = useI18n()
const { track } = useAmplitude()

const KlaviyoIntegrationsPage = defineAsyncComponent(() => import('@/pages/integrations/klaviyo.vue'))
const OmnisendIntegrationsPage = defineAsyncComponent(() => import('@/pages/integrations/omnisend.vue'))
const CartseeIntegrationsPage = defineAsyncComponent(() => import('@/pages/integrations/cartsee.vue'))
const ManbaIntegrationsPage = defineAsyncComponent(() => import('@/pages/integrations/manba.vue'))

const progressStore = useOnboardingRetentionProgressStore()
const { progress, loaded, resolvedEsp, selectedEspMeta } = storeToRefs(progressStore)

const i18nParams = computed(() => ({ esp: selectedEspMeta.value?.displayName ?? t('onboardingRetention.espBrandNames.klaviyo') }))

function espLogoUrlForCss(): string {
  const esp = resolvedEsp.value ?? 'klaviyo'
  if (esp === 'klaviyo')
    return `url('${klaviyoLogo}')`
  if (esp === 'cartsee')
    return `url('${cartseeLogo}')`
  if (esp === 'manba')
    return `url('${manbaLogo}')`
  return `url('/cherry/omnisend.svg')`
}

const espCssLogo = computed(espLogoUrlForCss)

const emptyStateTitleCss = computed(() => `"${t('onboardingRetention.step1.embedTitle', i18nParams.value)}"`)
const connectButtonCss = computed(() => `"${t('onboardingRetention.step1.connectButton', i18nParams.value)}"`)
const connectHintCss = computed(() => `"${t('onboardingRetention.step1.connectHint', i18nParams.value)}"`)

const EspIntegrationsPage = computed(() => {
  const esp = resolvedEsp.value ?? 'klaviyo'
  if (esp === 'klaviyo')
    return KlaviyoIntegrationsPage
  if (esp === 'omnisend')
    return OmnisendIntegrationsPage
  if (esp === 'manba')
    return ManbaIntegrationsPage
  return CartseeIntegrationsPage
})

const espAccountType = computed(() => {
  const esp = resolvedEsp.value ?? 'klaviyo'
  return RETENTION_ESP_META[esp].accountType
})

const { data: adsAccounts, refetch: refetchAdsAccounts } = useAdsAccountList()
const saving = ref(false)

const progressData = computed<Record<string, any>>(() => progress.value?.progress_data || {})

const hasConnectedAccount = computed(() => {
  if (progressData.value.step1Connected === true)
    return true
  if (!adsAccounts.value)
    return false
  return adsAccounts.value.some((account: any) => account.type === espAccountType.value)
})

const auditResponse = ref<RecaptureOnboardingAuditResponse | null>(null)
const auditStatus = ref<OnboardingAuditStatus | 'timeout' | null>(null)
const auditIsPolling = ref(false)
const auditTrackingReadySent = ref(false)
const auditPollingStartedAt = ref<number | null>(null)
const auditPollingTimer = ref<number | null>(null)
const auditAccountRefreshTimer = ref<number | null>(null)
const auditAccountRefreshStartedAt = ref<number | null>(null)

const auditWindowDays = 30
const auditMaxWaitMs = 30_000
const auditPollIntervalMs = 1_000
const RETENTION_AUDIT_CACHE_KEY = 'retention_onboarding_audit_cache_v1'

const auditTimezone = computed(() => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
  }
  catch {
    return 'UTC'
  }
})

const auditViewState = computed(() => {
  if (!hasConnectedAccount.value)
    return 'example' as const

  if (auditStatus.value === 'ready')
    return 'ready' as const

  if (auditStatus.value === 'failed')
    return 'failed' as const

  if (auditStatus.value === 'timeout')
    return 'timeout' as const

  return 'loading' as const
})

const auditEvents = computed(() => auditResponse.value?.events || [])
const auditCoveredEvents = computed(() => auditResponse.value?.coveredEvents || [])
const exampleAuditEvents = [
  { eventKey: 'Active on site', currentIdentifiedUniques30d: 9800, estimatedAdditionalIdentifiedUniques30d: 1800, currentRevenue30dUsd: 0, estimatedMonthlyIncrementalRevenueUsd: 0 },
  { eventKey: 'Product viewed', currentIdentifiedUniques30d: 5400, estimatedAdditionalIdentifiedUniques30d: 1200, currentRevenue30dUsd: 4200, estimatedMonthlyIncrementalRevenueUsd: 2100 },
  { eventKey: 'Collection viewed', currentIdentifiedUniques30d: 3600, estimatedAdditionalIdentifiedUniques30d: 850, currentRevenue30dUsd: 1800, estimatedMonthlyIncrementalRevenueUsd: 900 },
  { eventKey: 'Added to cart', currentIdentifiedUniques30d: 1600, estimatedAdditionalIdentifiedUniques30d: 420, currentRevenue30dUsd: 2400, estimatedMonthlyIncrementalRevenueUsd: 1200 },
  { eventKey: 'Checkout started', currentIdentifiedUniques30d: 980, estimatedAdditionalIdentifiedUniques30d: 260, currentRevenue30dUsd: 1200, estimatedMonthlyIncrementalRevenueUsd: 600 },
  { eventKey: 'Subscribe', currentIdentifiedUniques30d: 620, estimatedAdditionalIdentifiedUniques30d: 140, currentRevenue30dUsd: 400, estimatedMonthlyIncrementalRevenueUsd: 200 },
]

function sumAuditRevenue(values: (number | null | undefined)[]): number | null {
  if (values.length === 0)
    return null

  let total = 0
  for (const value of values) {
    // 明细不完整时不展示部分合计，以免把缺失收入误当作零。
    if (typeof value !== 'number' || !Number.isFinite(value))
      return null
    total += value
  }
  return total
}

interface AuditDisplayEvent {
  eventKey: string
  currentIdentifiedUniques30d: number | null
  estimatedAdditionalIdentifiedUniques30d: number | null
  currentRevenue30dUsd: number | null
  estimatedMonthlyIncrementalRevenueUsd: number | null
}

function groupOtherAuditEvents(rows: AuditDisplayEvent[]): AuditDisplayEvent[] {
  const otherKeys = new Set(['active on site', 'product viewed', 'collection viewed', 'subscribe', 'subscribed'])
  const isOther = (row: AuditDisplayEvent) => otherKeys.has(row.eventKey.trim().toLowerCase())
  const grouped = rows.filter(isOther)
  if (grouped.length === 0)
    return rows

  const visible = rows.filter(row => !isOther(row))
  const other: AuditDisplayEvent = {
    eventKey: 'Other events',
    currentIdentifiedUniques30d: sumAuditRevenue(grouped.map(row => row.currentIdentifiedUniques30d)),
    estimatedAdditionalIdentifiedUniques30d: sumAuditRevenue(grouped.map(row => row.estimatedAdditionalIdentifiedUniques30d)),
    currentRevenue30dUsd: sumAuditRevenue(grouped.map(row => row.currentRevenue30dUsd)),
    estimatedMonthlyIncrementalRevenueUsd: sumAuditRevenue(grouped.map(row => row.estimatedMonthlyIncrementalRevenueUsd)),
  }
  const checkoutIndex = visible.findIndex(row => row.eventKey.trim().toLowerCase() === 'checkout started')
  visible.splice(checkoutIndex < 0 ? visible.length : checkoutIndex + 1, 0, other)
  return visible
}

const exampleDisplayEvents = computed(() => groupOtherAuditEvents(exampleAuditEvents))

const auditDisplayEvents = computed(() => {
  const rows = auditEvents.value.map(row => ({
    eventKey: row.eventKey,
    currentIdentifiedUniques30d: row.nativePixelUniques30d ?? null,
    estimatedAdditionalIdentifiedUniques30d: row.EstimatedMonthlyIncrementalUniques ?? null,
    currentRevenue30dUsd: row.nativePixelRevenue30d,
    estimatedMonthlyIncrementalRevenueUsd: row.EstimatedMonthlyIncrementalRevenue,
  }))
  return groupOtherAuditEvents(rows)
})

const auditRevenueTotals = computed(() => ({
  currentRevenue: sumAuditRevenue(auditEvents.value.map(row => row.nativePixelRevenue30d)),
  estimatedRevenueLift: sumAuditRevenue(auditEvents.value.map(row => row.EstimatedMonthlyIncrementalRevenue)),
}))

const exampleRevenueTotals = computed(() => ({
  currentRevenue: sumAuditRevenue(exampleAuditEvents.map(row => row.currentRevenue30dUsd)),
  estimatedRevenueLift: sumAuditRevenue(exampleAuditEvents.map(row => row.estimatedMonthlyIncrementalRevenueUsd)),
}))

function formatCurrencyUsd(value: number | null | undefined) {
  if (value === null || value === undefined)
    return t('onboardingRetention.step1.audit.notAvailable')

  return new Intl.NumberFormat(locale.value === 'zh' ? 'zh-CN' : 'en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: value >= 100 ? 0 : 2,
  }).format(value)
}

function formatRevenueLift(value: number | null | undefined) {
  const formatted = formatCurrencyUsd(value)
  return value !== null && value !== undefined
    ? `+${formatted}`
    : formatted
}

function formatNumber(value: number | null | undefined) {
  if (value === null || value === undefined)
    return t('onboardingRetention.step1.audit.notAvailable')

  return new Intl.NumberFormat(locale.value === 'zh' ? 'zh-CN' : 'en-US').format(value)
}

function formatPercent(value: number | null | undefined) {
  if (value === null || value === undefined)
    return t('onboardingRetention.step1.audit.notAvailable')

  return new Intl.NumberFormat(locale.value === 'zh' ? 'zh-CN' : 'en-US', {
    style: 'percent',
    maximumFractionDigits: 1,
  }).format(value)
}

function normalizeEventKeyLabel(eventKey: string) {
  const normalized = String(eventKey || '').trim().toLowerCase()
  if (normalized === 'active on site')
    return t('onboardingRetention.step1.audit.events.activeOnSite')
  if (normalized === 'product viewed')
    return t('onboardingRetention.step1.audit.events.productViewed')
  if (normalized === 'collection viewed')
    return t('onboardingRetention.step1.audit.events.collectionViewed')
  if (normalized === 'added to cart')
    return t('onboardingRetention.step1.audit.events.addedToCart')
  if (normalized === 'checkout started')
    return t('onboardingRetention.step1.audit.events.checkoutStarted')
  if (normalized === 'subscribe' || normalized === 'subscribed')
    return t('onboardingRetention.step1.audit.events.subscribe')
  if (normalized === 'other events')
    return t('onboardingRetention.step1.audit.events.otherEvents')

  return t('onboardingRetention.step1.audit.events.unknown', { event: eventKey })
}

function stopAuditPolling() {
  if (auditPollingTimer.value) {
    window.clearTimeout(auditPollingTimer.value)
    auditPollingTimer.value = null
  }
  auditIsPolling.value = false
  auditPollingStartedAt.value = null
}

async function pollAuditOnce() {
  const startedAt = auditPollingStartedAt.value
  if (!startedAt)
    return

  if (Date.now() - startedAt > auditMaxWaitMs) {
    stopAuditPolling()
    auditStatus.value = 'timeout'
    return
  }

  try {
    const result = await recaptureOnboardingAuditApi({
      window_days: auditWindowDays,
      timezone: auditTimezone.value,
    })

    auditResponse.value = result
    auditStatus.value = result.status

    if (result.status === 'ready') {
      try {
        window.sessionStorage.setItem(RETENTION_AUDIT_CACHE_KEY, JSON.stringify({
          refreshedAt: result.refreshedAt ?? null,
          windowDays: result.windowDays ?? auditWindowDays,
          abandonmentFlowRevenue30dUsd: result.abandonmentFlowRevenue30dUsd ?? null,
          upliftCoefficient: result.upliftCoefficient ?? null,
          estimatedMonthlyIncrementalRevenueUsd: result.estimatedMonthlyIncrementalRevenueUsd ?? null,
        }))
      }
      catch {
      }
      stopAuditPolling()
      if (!auditTrackingReadySent.value) {
        auditTrackingReadySent.value = true
        track('estimate_ready', {
          source: 'retention_onboarding_step1',
          refreshed_at: result.refreshedAt ?? null,
          window_days: result.windowDays ?? auditWindowDays,
          estimated_monthly_incremental_revenue_usd: result.estimatedMonthlyIncrementalRevenueUsd ?? null,
          abandonment_flow_revenue_30d_usd: result.abandonmentFlowRevenue30dUsd ?? null,
          admission_recommendation: result.admissionRecommendation ?? null,
          status: result.status,
        })
      }
      return
    }

    if (result.status === 'failed') {
      stopAuditPolling()
      return
    }
  }
  catch {
    stopAuditPolling()
    auditStatus.value = 'failed'
    return
  }

  auditPollingTimer.value = window.setTimeout(pollAuditOnce, auditPollIntervalMs)
}

function startAuditPolling() {
  if (!hasConnectedAccount.value)
    return
  if (auditIsPolling.value)
    return
  if (auditStatus.value === 'ready')
    return

  auditIsPolling.value = true
  auditStatus.value = 'pending'
  auditPollingStartedAt.value = Date.now()
  pollAuditOnce()
}

function stopAccountRefreshPolling() {
  if (auditAccountRefreshTimer.value) {
    window.clearInterval(auditAccountRefreshTimer.value)
    auditAccountRefreshTimer.value = null
  }
  auditAccountRefreshStartedAt.value = null
}

function startAccountRefreshPolling() {
  if (auditAccountRefreshTimer.value)
    return
  auditAccountRefreshStartedAt.value = Date.now()
  auditAccountRefreshTimer.value = window.setInterval(async () => {
    if (hasConnectedAccount.value) {
      stopAccountRefreshPolling()
      return
    }
    const started = auditAccountRefreshStartedAt.value
    if (!started || Date.now() - started > 60_000) {
      stopAccountRefreshPolling()
      return
    }
    await refetchAdsAccounts()
  }, 2_000)
}

async function refreshConnectedAccounts() {
  try {
    await refetchAdsAccounts()
  }
  catch {
  }
}

function handleWindowFocus() {
  void refreshConnectedAccounts()
}

function handleVisibilityChange() {
  if (document.visibilityState === 'visible')
    void refreshConnectedAccounts()
}

const completedSteps = computed(() => {
  const out: number[] = []
  if (hasConnectedAccount.value)
    out.push(1)
  if (progressData.value.step2ConversionFeedConfigured === true)
    out.push(2)
  if (progressData.value.step3AppEmbedEnabled === true)
    out.push(3)
  if (progress.value?.is_completed)
    out.push(4)
  return out
})
const clickableSteps = computed(() => {
  return []
})

async function ensureLoaded() {
  if (loaded.value)
    return
  let result: any = null
  try {
    result = await progressStore.loadFromBackend()
  }
  catch (e) {
    console.warn('[retention-onboarding step1] loadFromBackend failed, fallback local:', e)
  }
  if (!resolvedEsp.value) {
    if (result !== null) {
      router.replace('/onboarding-retention/step0')
      return
    }
    progressStore.setEsp('klaviyo')
    return
  }
  if (result === 'completed') {
    router.replace('/onboarding-retention/step4')
  }
}

async function saveProgress(nextStep: number, patch: Record<string, any>) {
  const merged = {
    ...(progress.value?.progress_data || {}),
    ...patch,
  }
  await progressStore.save({
    current_step: nextStep,
    current_platform: 'retention',
    progress_data: merged,
  })
  if (progress.value) {
    progress.value = {
      ...progress.value,
      current_step: nextStep,
      current_platform: 'retention',
      progress_data: merged,
    }
  }
}

async function next() {
  if (saving.value)
    return
  track('retention_onboarding_step1_continue_click')
  saving.value = true
  try {
    await refetchAdsAccounts()
    if (!hasConnectedAccount.value)
      return
    await saveProgress(2, { step1Connected: true })
    router.push({ path: '/onboarding-retention/step2', query: { prev: route.fullPath } })
  }
  finally {
    saving.value = false
  }
}

function back() {
  track('retention_onboarding_step1_back_click', { esp: resolvedEsp.value ?? 'klaviyo' })
  router.push('/onboarding-retention/step0')
}

function handleEspConnectClick(event: MouseEvent) {
  const target = event.target as HTMLElement | null
  const el = target?.closest('button') as HTMLElement | null
  const label = el?.textContent?.trim()
  if (!label)
    return
  const connectLabel = t('onboardingRetention.step1.connectButton', i18nParams.value).trim()
  if (label.includes(connectLabel) || /Connect\s+\w+\s+Account/i.test(label) || /Collect\s+\w+\s+Account/i.test(label)) {
    track('retention_onboarding_step1_connect_click', { esp: resolvedEsp.value ?? 'klaviyo' })
    startAccountRefreshPolling()
  }
}

onMounted(async () => {
  track('retention_onboarding_step1_pageview', { esp: resolvedEsp.value ?? 'klaviyo' })
  track('estimate_viewed', { source: 'retention_onboarding_step1' })
  await ensureLoaded()
  if (!resolvedEsp.value)
    return
  await refreshConnectedAccounts()
  window.addEventListener('focus', handleWindowFocus)
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

watch(hasConnectedAccount, (connected) => {
  if (connected) {
    stopAccountRefreshPolling()
    startAuditPolling()
  }
}, { immediate: true })

onBeforeUnmount(() => {
  stopAuditPolling()
  stopAccountRefreshPolling()
  window.removeEventListener('focus', handleWindowFocus)
  document.removeEventListener('visibilitychange', handleVisibilityChange)
})
</script>

<template>
  <OnboardingRetentionLayout
    class="retention-step1"
    :current-step="1"
    :completed-steps="completedSteps"
    :clickable-steps="clickableSteps"
    title-key="onboardingRetention.step1.title"
    subtitle-key="onboardingRetention.step1.subtitle"
  >
    <div class="space-y-4">
      <div class="rounded-[var(--p-border-radius-200)] bg-white border border-black overflow-hidden">
        <div v-if="hasConnectedAccount" class="p-3 flex items-center justify-end gap-3">
          <div class="inline-flex items-center gap-2 text-sm text-emerald-700">
            <Icon name="lucide:check-circle-2" class="h-4 w-4" />
            <span>{{ t('onboardingRetention.status.connected') }}</span>
          </div>
        </div>
        <div
          class="p-3 retention-connect-card"
          :class="{ 'retention-manual-connect': resolvedEsp === 'cartsee' || resolvedEsp === 'manba' }"
          data-testid="onboarding-retention-step1-esp"
          :style="{ '--esp-empty-title': emptyStateTitleCss, '--esp-connect-btn': connectButtonCss, '--esp-connect-hint': connectHintCss, '--esp-logo': espCssLogo }"
          @click.capture="handleEspConnectClick"
        >
          <component :is="EspIntegrationsPage" :show-guide-card="false" />
        </div>
        <div
          v-if="!hasConnectedAccount"
          data-testid="onboarding-retention-step1-privacy"
          class="w-full bg-[#EDF8E5] px-5 py-4"
        >
          <h3 class="flex items-center gap-2 text-sm font-semibold text-[#111827]">
            <Icon name="lucide:lock-keyhole" class="h-4 w-4 shrink-0" aria-hidden="true" />
            {{ t('onboardingRetention.step1.klaviyoPrivacy.title') }}
          </h3>
          <ul class="mt-3 space-y-[2px] text-sm leading-snug text-[#6D6D6D]">
            <li class="flex items-start gap-2">
              <Icon name="lucide:check" color="#008060" class="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{{ t('onboardingRetention.step1.platformPrivacy.dataAccess', i18nParams) }}</span>
            </li>
            <li class="flex items-start gap-2">
              <Icon name="lucide:check" color="#008060" class="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{{ t('onboardingRetention.step1.klaviyoPrivacy.unchanged') }}</span>
            </li>
          </ul>
        </div>
      </div>

      <div
        data-testid="onboarding-retention-step1-testimonials"
        class="klaviyo-testimonials"
      >
        <figure class="klaviyo-testimonial">
          <blockquote class="klaviyo-testimonial__quote">
            {{ t('onboardingRetention.step1.testimonials.sylvox.quote') }}
          </blockquote>
          <figcaption class="klaviyo-testimonial__footer">
            <div class="klaviyo-testimonial__person">
              <img :src="rebeccaPortrait" :alt="t('onboardingRetention.step1.testimonials.sylvox.name')" class="klaviyo-testimonial__avatar" width="44" height="44">
              <div class="text-[13px] font-medium leading-snug">
                {{ t('onboardingRetention.step1.testimonials.attribution', { name: t('onboardingRetention.step1.testimonials.sylvox.name'), role: t('onboardingRetention.step1.testimonials.sylvox.role') }) }}
              </div>
            </div>
            <img :src="sylvoxLogo" :alt="t('onboardingRetention.step1.testimonials.sylvox.brand')" class="klaviyo-testimonial__logo">
          </figcaption>
        </figure>
        <figure class="klaviyo-testimonial">
          <blockquote class="klaviyo-testimonial__quote">
            {{ t('onboardingRetention.step1.testimonials.narwal.quote') }}
          </blockquote>
          <figcaption class="klaviyo-testimonial__footer">
            <div class="klaviyo-testimonial__person">
              <img :src="felixPortrait" :alt="t('onboardingRetention.step1.testimonials.narwal.name')" class="klaviyo-testimonial__avatar" width="44" height="44">
              <div class="text-[13px] font-medium leading-snug">
                {{ t('onboardingRetention.step1.testimonials.attribution', { name: t('onboardingRetention.step1.testimonials.narwal.name'), role: t('onboardingRetention.step1.testimonials.narwal.role') }) }}
              </div>
            </div>
            <img :src="narwalLogo" :alt="t('onboardingRetention.step1.testimonials.narwal.brand')" class="klaviyo-testimonial__logo klaviyo-testimonial__logo--narwal">
          </figcaption>
        </figure>
      </div>

      <div v-if="resolvedEsp === 'cartsee' || resolvedEsp === 'manba'" class="rounded-[var(--p-border-radius-300)] border border-black/10 bg-[#F5F4ED] px-6 py-5 space-y-3">
        <p class="text-sm text-[#1F2937] leading-relaxed">
          {{ t(`onboardingRetention.step1.${resolvedEsp}.limitationParagraph`) }}
        </p>
        <div class="space-y-2 pt-1">
          <p class="text-sm text-[#1F2937] leading-relaxed">
            {{ t(`onboardingRetention.step1.${resolvedEsp}.resultsLeadIn`) }}
          </p>
          <ul class="list-disc pl-5 space-y-1.5 text-sm text-[#1F2937] leading-relaxed">
            <li>{{ t(`onboardingRetention.step1.${resolvedEsp}.results.item1`) }}</li>
            <li>{{ t(`onboardingRetention.step1.${resolvedEsp}.results.item2`) }}</li>
          </ul>
        </div>
      </div>

      <Card v-if="resolvedEsp !== 'cartsee' && resolvedEsp !== 'manba'" class="rounded-[var(--p-border-radius-300)] border border-black bg-white shadow-none overflow-hidden">
        <CardHeader class="pb-0">
          <div class="flex items-start justify-between gap-3">
            <div class="space-y-1">
              <CardTitle class="text-base">
                {{ t('onboardingRetention.step1.audit.recoveryTitle') }}
              </CardTitle>
              <div v-if="auditViewState === 'example'" class="text-sm text-[#6D6D6D] leading-snug">
                {{ t('onboardingRetention.step1.audit.exampleRevenueSubtitle', i18nParams) }}
              </div>
            </div>
            <div
              v-if="auditViewState === 'example'"
              class="shrink-0 rounded-full bg-[#F5F4ED] px-3 py-1 text-[11px] font-semibold tracking-[0.14em] text-[#6D6D6D]"
            >
              {{ t('onboardingRetention.step1.audit.badges.example') }}
            </div>
          </div>
        </CardHeader>

        <CardContent class="pt-4">
          <div v-if="auditViewState === 'example'" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div class="rounded-[var(--p-border-radius-300)] border border-black/10 bg-white px-4 py-3">
                <div class="text-[12px] font-semibold text-[#6D6D6D]">
                  {{ t('onboardingRetention.step1.audit.kpis.abandonmentFlowRevenue30dUsd') }}
                </div>
                <div class="mt-1 text-[18px] font-semibold text-[#111827]">
                  {{ formatCurrencyUsd(exampleRevenueTotals.currentRevenue) }}
                </div>
              </div>
              <div class="rounded-[var(--p-border-radius-300)] bg-[#EDF8E5] px-4 py-3">
                <div class="text-[12px] font-semibold text-[#6D6D6D]">
                  {{ t('onboardingRetention.step1.audit.kpis.estimatedMonthlyIncrementalRevenueUsd') }}
                </div>
                <div class="mt-1 text-[18px] font-semibold text-[#111827]">
                  {{ formatRevenueLift(exampleRevenueTotals.estimatedRevenueLift) }}
                </div>
              </div>
            </div>

            <div class="overflow-hidden rounded-[var(--p-border-radius-300)] border border-black/10">
              <div class="grid grid-cols-[1.3fr_1fr_1fr_1fr_1fr] px-4 py-2 text-[12px] font-semibold text-[#6D6D6D]">
                <div>{{ t('onboardingRetention.step1.audit.table.event') }}</div>
                <div class="text-right">
                  {{ t('onboardingRetention.step1.audit.table.currentEventsIdentified') }}
                </div>
                <div class="text-right">
                  {{ t('onboardingRetention.step1.audit.table.estimatedEventsLift') }}
                </div>
                <div class="text-right">
                  {{ t('onboardingRetention.step1.audit.table.currentRevenue') }}
                </div>
                <div class="text-right">
                  {{ t('onboardingRetention.step1.audit.table.estimatedRevenueLift') }}
                </div>
              </div>
              <div class="divide-y divide-black/5 bg-white">
                <div
                  v-for="row in exampleDisplayEvents"
                  :key="row.eventKey"
                  class="grid grid-cols-[1.3fr_1fr_1fr_1fr_1fr] px-4 py-2 text-sm text-[#111827]"
                >
                  <div class="font-medium">
                    {{ normalizeEventKeyLabel(row.eventKey) }}
                  </div>
                  <div class="text-right">
                    {{ formatNumber(row.currentIdentifiedUniques30d) }}
                  </div>
                  <div class="text-right">
                    {{ formatNumber(row.estimatedAdditionalIdentifiedUniques30d) }}
                  </div>
                  <div class="text-right">
                    {{ formatCurrencyUsd(row.currentRevenue30dUsd) }}
                  </div>
                  <div class="text-right">
                    {{ formatCurrencyUsd(row.estimatedMonthlyIncrementalRevenueUsd) }}
                  </div>
                </div>
              </div>
            </div>

            <div class="text-xs text-[#6D6D6D] leading-snug">
              {{ t('onboardingRetention.step1.audit.estimatedDisclaimer') }}
            </div>
          </div>

          <div v-else class="space-y-4">
            <div v-if="auditViewState === 'loading'" class="rounded-[var(--p-border-radius-300)] border border-black/10 bg-white px-4 py-4">
              <div class="flex items-center gap-3">
                <Icon name="lucide:loader-2" class="h-4 w-4 animate-spin text-[#6D6D6D]" />
                <div class="text-sm text-[#111827] font-medium">
                  {{ t('onboardingRetention.step1.audit.loading.title') }}
                </div>
              </div>
              <div class="mt-2 text-sm text-[#6D6D6D] leading-snug">
                {{ t('onboardingRetention.step1.audit.loading.subtitle') }}
              </div>
            </div>

            <div v-else-if="auditViewState === 'failed'" class="rounded-[var(--p-border-radius-300)] border border-black/10 bg-white px-4 py-4">
              <div class="flex items-center gap-3">
                <Icon name="lucide:alert-triangle" class="h-4 w-4 text-[#8F2C2C]" />
                <div class="text-sm text-[#111827] font-medium">
                  {{ t('onboardingRetention.step1.audit.failed.title') }}
                </div>
              </div>
              <div class="mt-2 text-sm text-[#6D6D6D] leading-snug">
                {{ t('onboardingRetention.step1.audit.failed.subtitle') }}
              </div>
              <div class="mt-3">
                <Button variant="outline" class="bg-white border-black text-black shadow-none hover:bg-white" @click="startAuditPolling">
                  {{ t('onboardingRetention.step1.audit.actions.retry') }}
                </Button>
              </div>
            </div>

            <div v-else-if="auditViewState === 'timeout'" class="rounded-[var(--p-border-radius-300)] border border-black/10 bg-white px-4 py-4">
              <div class="flex items-center gap-3">
                <Icon name="lucide:clock" class="h-4 w-4 text-[#6D6D6D]" />
                <div class="text-sm text-[#111827] font-medium">
                  {{ t('onboardingRetention.step1.audit.timeout.title') }}
                </div>
              </div>
              <div class="mt-2 text-sm text-[#6D6D6D] leading-snug">
                {{ t('onboardingRetention.step1.audit.timeout.subtitle') }}
              </div>
              <div class="mt-3">
                <Button variant="outline" class="bg-white border-black text-black shadow-none hover:bg-white" @click="startAuditPolling">
                  {{ t('onboardingRetention.step1.audit.actions.retry') }}
                </Button>
              </div>
            </div>

            <div v-else class="space-y-4">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div class="rounded-[var(--p-border-radius-300)] border border-black/10 bg-white px-4 py-3">
                  <div class="text-[12px] font-semibold text-[#6D6D6D]">
                    {{ t('onboardingRetention.step1.audit.kpis.abandonmentFlowRevenue30dUsd') }}
                  </div>
                  <div class="mt-1 text-[18px] font-semibold text-[#111827]">
                    {{ formatCurrencyUsd(auditRevenueTotals.currentRevenue) }}
                  </div>
                </div>
                <div class="rounded-[var(--p-border-radius-300)] bg-[#EDF8E5] px-4 py-3">
                  <div class="text-[12px] font-semibold text-[#6D6D6D]">
                    {{ t('onboardingRetention.step1.audit.kpis.estimatedMonthlyIncrementalRevenueUsd') }}
                  </div>
                  <div class="mt-1 text-[18px] font-semibold text-[#111827]">
                    {{ formatRevenueLift(auditRevenueTotals.estimatedRevenueLift) }}
                  </div>
                </div>
              </div>

              <div class="flex flex-wrap items-center justify-between gap-2 rounded-[var(--p-border-radius-300)] bg-[#F5F4ED] px-4 py-3 text-xs text-[#6D6D6D]">
                <div class="flex items-center gap-2">
                  <span class="font-semibold">{{ t('onboardingRetention.step1.audit.meta.refreshedAt') }}</span>
                  <span>{{ auditResponse?.refreshedAt || t('onboardingRetention.step1.audit.notAvailable') }}</span>
                </div>
                <div class="flex items-center gap-2">
                  <span class="font-semibold">{{ t('onboardingRetention.step1.audit.meta.windowDays') }}</span>
                  <span>{{ auditResponse?.windowDays ?? auditWindowDays }}</span>
                </div>
              </div>

              <div v-if="auditCoveredEvents.length > 0" class="rounded-[var(--p-border-radius-300)] border border-black/10 bg-white px-4 py-3">
                <div class="text-xs font-semibold text-[#6D6D6D]">
                  {{ t('onboardingRetention.step1.audit.coveredEvents.title') }}
                </div>
                <div class="mt-2 flex flex-wrap gap-2">
                  <span
                    v-for="ev in auditCoveredEvents"
                    :key="ev"
                    class="rounded-full bg-[#F5F4ED] px-3 py-1 text-xs text-[#6D6D6D]"
                  >
                    {{ normalizeEventKeyLabel(ev) }}
                  </span>
                </div>
              </div>

              <div class="overflow-hidden rounded-[var(--p-border-radius-300)] border border-black/10">
                <div class="grid grid-cols-[1.3fr_1fr_1fr_1fr_1fr] px-4 py-2 text-[12px] font-semibold text-[#6D6D6D]">
                  <div>{{ t('onboardingRetention.step1.audit.table.event') }}</div>
                  <div class="text-right">
                    {{ t('onboardingRetention.step1.audit.table.currentEventsIdentified') }}
                  </div>
                  <div class="text-right">
                    {{ t('onboardingRetention.step1.audit.table.estimatedEventsLift') }}
                  </div>
                  <div class="text-right">
                    {{ t('onboardingRetention.step1.audit.table.currentRevenue') }}
                  </div>
                  <div class="text-right">
                    {{ t('onboardingRetention.step1.audit.table.estimatedRevenueLift') }}
                  </div>
                </div>
                <div v-if="auditDisplayEvents.length === 0" class="px-4 py-4 text-sm text-[#6D6D6D]">
                  {{ t('onboardingRetention.step1.audit.empty') }}
                </div>
                <div v-else class="divide-y divide-black/5 bg-white">
                  <div
                    v-for="row in auditDisplayEvents"
                    :key="row.eventKey"
                    class="grid grid-cols-[1.3fr_1fr_1fr_1fr_1fr] px-4 py-2 text-sm text-[#111827]"
                  >
                    <div class="font-medium">
                      {{ normalizeEventKeyLabel(row.eventKey) }}
                    </div>
                    <div class="text-right">
                      {{ formatNumber(row.currentIdentifiedUniques30d) }}
                    </div>
                    <div class="text-right">
                      {{ formatNumber(row.estimatedAdditionalIdentifiedUniques30d) }}
                    </div>
                    <div class="text-right">
                      {{ formatCurrencyUsd(row.currentRevenue30dUsd) }}
                    </div>
                    <div class="text-right">
                      {{ formatCurrencyUsd(row.estimatedMonthlyIncrementalRevenueUsd) }}
                    </div>
                  </div>
                </div>
              </div>

              <div class="text-xs text-[#6D6D6D] leading-snug">
                {{ t('onboardingRetention.step1.audit.estimatedDisclaimer') }}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <div class="sticky bottom-0 bg-white pt-4 pb-2">
        <div class="flex items-center justify-between gap-3">
          <Button variant="outline" class="bg-white border-black text-black shadow-none hover:bg-white" @click="back">
            {{ t('onboardingRetention.actions.back') }}
          </Button>
          <Button
            :disabled="!hasConnectedAccount || saving"
            :class="(hasConnectedAccount && !saving) ? 'bg-black hover:bg-black/90 shadow-none' : 'bg-[#6D6D6D] hover:bg-[#5d5d5d] shadow-none'"
            @click="next"
          >
            <Icon v-if="saving" name="lucide:loader-2" class="h-4 w-4 mr-2 animate-spin" />
            {{ t('onboardingRetention.actions.continue') }}
          </Button>
        </div>
      </div>
    </div>

    <template #right>
      <div class="h-full w-full bg-[#F5F4ED] text-[#6D6D6D]">
        <div class="px-8 pt-10">
          <div class="text-[10px] font-semibold tracking-[0.18em]">
            {{ t('onboardingRetention.step1.right.kicker') }}
          </div>
        </div>

        <div class="px-8 pt-4">
          <div class="space-y-5">
            <div class="space-y-3">
              <div class="h-3 w-full overflow-hidden rounded-full bg-[#E6E6E6]">
                <div class="h-full rounded-full bg-[#6B7280]" style="width: 15%" />
              </div>
              <div class="flex items-center justify-between gap-6 text-[14px] leading-snug">
                <span>{{ t('onboardingRetention.step1.right.progressLabel', i18nParams) }}</span>
                <span>15%</span>
              </div>
            </div>
            <div class="space-y-3">
              <div class="h-3 w-full overflow-hidden rounded-full bg-[#E6E6E6]">
                <div class="h-full rounded-full bg-[#2FD26B]" style="width: 55%" />
              </div>
              <div class="flex items-center justify-between gap-6 text-[14px] leading-snug">
                <span>{{ t('onboardingRetention.step1.connected.progressLabel', i18nParams) }}</span>
                <span>55%</span>
              </div>
            </div>
          </div>
        </div>

        <div class="px-8 pt-4 pb-10">
          <div class="rounded-[var(--p-border-radius-300)] bg-[rgba(226,226,226,0.55)] px-6 py-6">
            <div class="text-[14px] leading-snug whitespace-pre-line">
              {{ t('onboardingRetention.step1.right.callout', i18nParams) }}
            </div>
          </div>
        </div>
      </div>
    </template>
  </OnboardingRetentionLayout>
</template>

<style scoped>
.klaviyo-testimonials {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 320px), 1fr));
  gap: 16px;
}
.klaviyo-testimonial {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  margin: 0;
  padding: 20px;
  border: 1px solid #000000;
  border-radius: 12px;
  background: #EDF8E5;
  color: #111827;
}
.klaviyo-testimonial__quote {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
  overflow-wrap: anywhere;
}
.klaviyo-testimonial__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.klaviyo-testimonial__person {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.klaviyo-testimonial__avatar {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 50%;
  object-fit: cover;
}
.klaviyo-testimonial__logo {
  width: 82px;
  max-width: 30%;
  max-height: 30px;
  flex-shrink: 0;
  object-fit: contain;
}
.klaviyo-testimonial__logo--narwal {
  width: 108.486px;
  max-width: 39.69%;
  max-height: 39.69px;
}
.retention-step1 :deep(section > div:first-child > p) { max-width: 100%; }
:deep(.mt-6) { margin-top: 0 !important; }
:deep(.flex.items-center.justify-between.mb-6) { display: none !important; }
:deep(.mt-6.flex.gap-3 > button):nth-child(2) { display: none !important; }
:deep(.flex.flex-col.items-center.justify-center.py-16) { padding-top: 1.75rem !important; padding-bottom: 1.75rem !important; }
:deep([data-testid="onboarding-retention-step1-esp"] h3.mt-6) { font-size: 0 !important; margin-top: 0.75rem !important; }
:deep([data-testid="onboarding-retention-step1-esp"] h3.mt-6)::before { content: var(--esp-empty-title); font-size: 16px; line-height: 1.25; font-weight: 600; color: #111827; display: block; text-align: center; }
:deep([data-testid="onboarding-retention-step1-esp"] p.mt-2.text-center) { font-size: 0 !important; }
:deep([data-testid="onboarding-retention-step1-esp"] p.mt-2.text-center) { margin-top: 0.5rem !important; }
:deep([data-testid="onboarding-retention-step1-esp"] p.mt-2.text-center)::before { content: var(--esp-connect-hint); font-size: 14px; line-height: 1.35; color: #6D6D6D; white-space: pre-line; display: block; }
.retention-connect-card :deep(p.mt-2.text-center) { display: none !important; }
.retention-manual-connect :deep(.grid > :last-child [data-slot="card-title"]) { font-size: 0; }
.retention-manual-connect :deep(.grid > :last-child [data-slot="card-title"])::before { content: var(--esp-empty-title); font-size: 16px; line-height: 1.25; display: block; }
:deep([data-testid="onboarding-retention-step1-esp"] .bg-card) { background: transparent !important; }
:deep([data-testid="onboarding-retention-step1-esp"] .border) { border-width: 0 !important; }
:deep([data-testid="onboarding-retention-step1-esp"] .shadow) { box-shadow: none !important; }
:deep([data-testid="onboarding-retention-step1-esp"] .shadow-sm) { box-shadow: none !important; }
:deep([data-testid="onboarding-retention-step1-esp"] .shadow-md) { box-shadow: none !important; }
:deep([data-testid="onboarding-retention-step1-esp"] .mt-6.flex.gap-3) { margin-top: 1.75rem !important; flex-direction: column !important; align-items: center !important; gap: 0.5rem !important; }
:deep([data-testid="onboarding-retention-step1-esp"] .mt-6.flex.gap-3 > button) { box-shadow: none !important; }
:deep([data-testid="onboarding-retention-step1-esp"] .mt-6.flex.gap-3 > button):first-child { font-size: 0 !important; }
:deep([data-testid="onboarding-retention-step1-esp"] .mt-6.flex.gap-3 > button):first-child::after { content: var(--esp-connect-btn); font-size: 16px; font-weight: 600; line-height: 1.2; }
:deep([data-testid="onboarding-retention-step1-esp"] .mx-auto.flex.h-20.w-20.items-center.justify-center.rounded-full.bg-muted) { width: 40px !important; height: 40px !important; background: transparent !important; border-radius: 0 !important; background-image: var(--esp-logo) !important; background-size: contain !important; background-repeat: no-repeat !important; background-position: center !important; }
:deep([data-testid="onboarding-retention-step1-esp"] .mx-auto.flex.h-20.w-20.items-center.justify-center.rounded-full.bg-muted > *) { display: none !important; }
</style>
